import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import { useLanguage } from '../context/LanguageContext';

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { toast } = useToast();
  const { language } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      await login(formData.email, formData.password);
      toast({
        title: language === 'es' ? 'Inicio de sesión exitoso' : 'Login successful',
        description: language === 'es' ? 'Bienvenido de vuelta' : 'Welcome back'
      });
      navigate('/dashboard');
    } catch (error) {
      console.error('Login error:', error);
      toast({
        title: language === 'es' ? 'Error de inicio de sesión' : 'Login error',
        description: error.response?.data?.detail || (language === 'es' ? 'Credenciales inválidas' : 'Invalid credentials'),
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <div className="min-h-screen bg-sand-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <Card className="w-full max-w-md border-2 border-gray-200">
        <CardHeader className="text-center">
          <CardTitle className="text-3xl font-bold">
            {language === 'es' ? 'Iniciar Sesión' : 'Sign In'}
          </CardTitle>
          <CardDescription>
            {language === 'es' ? 'Ingresa a tu cuenta de ColaboraChile' : 'Enter your ColaboraChile account'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">{language === 'es' ? 'Correo electrónico' : 'Email'}</Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                className="border-2 border-gray-200"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">{language === 'es' ? 'Contraseña' : 'Password'}</Label>
              <Input
                id="password"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleInputChange}
                required
                className="border-2 border-gray-200"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-secondary text-white hover:bg-secondary/90"
            >
              {loading ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> {language === 'es' ? 'Iniciando sesión...' : 'Signing in...'}</>
              ) : (
                language === 'es' ? 'Iniciar Sesión' : 'Sign In'
              )}
            </Button>

            <div className="text-center text-sm text-gray-600 mt-4">
              {language === 'es' ? '¿No tienes cuenta?' : "Don't have an account?"}{' '}
              <Link to="/register" className="text-primary hover:underline font-semibold">
                {language === 'es' ? 'Regístrate' : 'Sign up'}
              </Link>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default Login;
